import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../utils/customToast';
import { useAuth } from '../context/AuthContext';

export const useApiErrorHandler = () => {
    const navigate = useNavigate();
    const { logout, loggingOut } = useAuth();

    const handleApiError = useCallback((error, response = null) => {
        if (response && !response.ok) {
            const statusCode = response.status;

            if (statusCode === 401) {
                // Logout volontaire en cours → ne pas afficher "Session expirée"
                if (loggingOut?.current) return;
                toast.error('Session expirée. Veuillez vous reconnecter.', {
                    position: "top-right",
                    autoClose: 5000,
                });
                logout();
                navigate('/');
                return;
            }

            if (statusCode === 403 && error.code === 'SUSPENDED') {
                // Pour les organisations suspendues, ne pas rediriger automatiquement
                // Le statut sera affiché dans le header, les utilisateurs peuvent rester
                toast.warning('Organisation suspendue - Fonctionnalités limitées', {
                    position: "top-right",
                    autoClose: 5000,
                });
                // Pas de redirection automatique
                return;
            }

            // Note: Le statut 'cancelled' n'existe plus - les comptes annulés deviennent 'free' + 'active'

            // Ne pas afficher de toast pour NO_TENANT car c'est normal pour un nouveau compte
            // Les pages individuelles gèrent cet état avec un message convivial
            if (statusCode === 400 && error.code === 'NO_TENANT') {
                // Silencieusement ignorer - les pages affichent leur propre UI
                return;
            }

            // Ne pas afficher de toast pour 403/400 sur les ressources tenant quand pas de tenant actif
            // Cela évite les erreurs inutiles lors de la création d'un nouveau compte
            if ((statusCode === 403 || statusCode === 400) && 
                (error.code === 'NO_ACTIVE_TENANT' || error.message?.includes('tenant'))) {
                // Silencieusement ignorer - les pages affichent leur propre UI
                return;
            }

            // Gestion des limites atteintes - NE PAS afficher de toast
            // Les composants gèrent eux-mêmes l'affichage des messages d'erreur
            if (statusCode === 403 && (
                error.code === 'LIMIT_REACHED' || 
                error.code === 'USER_LIMIT_REACHED' || 
                error.code === 'PARTIAL_LIMIT'
            )) {
                // Ne rien faire, laisser le composant gérer l'affichage
                return;
            }
        }

        // Message par défaut uniquement si non géré ci-dessus
        toast.error(error.message || 'Une erreur est survenue', {
            position: "top-right",
            autoClose: 5000,
        });
    }, [navigate, logout, loggingOut]);

    return { handleApiError };
};